import * as React from 'react';

import moment from 'moment';

import config from 'js/app/config';
import Imgix from 'js/lib/imgix';
import path from 'js/lib/path';
import { useLocation } from 'js/lib/useRouter';

import { getLink } from 'bundles/graphql/utils/InstructorsV1';
import { clearEmptyFields } from 'bundles/seo/utils/utils';
import { DEFAULT_VLP_META_DESCRIPTION } from 'bundles/video-logged-out-page/constants';
import type {
  Course,
  Instructor,
  Partner,
  VLPItem,
  VLPLesson,
  VLPModule,
} from 'bundles/video-logged-out-page/types/vlpSharedTypes';
import { constructFallBackDescriptionForVlp } from 'bundles/video-logged-out-page/utils/utils';

export type Resolution = {
  id: string;
  webMVideoUrl?: string | null;
  mp4VideoUrl?: string | null;
  previewImageUrl: string;
};

type InputProps = {
  course: Course;
  courseName?: string;
  courseSlug: string;
  partner: Partner;
  instructors: Array<Instructor>;
  videoItem: VLPItem;
  lesson?: VLPLesson;
  module?: VLPModule;
  onDemandVideosSpec: {
    sources?: { byResolution?: Record<string, Resolution> };
  };
  textTranscript: string;
};

type Props = InputProps;

const getInstructorNames = (instructors: Array<Instructor>) => {
  if (!instructors) {
    return [];
  }

  return instructors.map((instructor: Instructor) => instructor.fullName);
};

const getInstructorURLs = (origin: string, instructors: Array<Instructor>) => {
  if (!instructors) {
    return [];
  }

  return instructors.map((instructor: Instructor) => {
    const instructorLink = getLink(instructor);
    return instructorLink && path.join(origin, instructorLink);
  });
};

const getOrganizationGraphType = (origin: string) => {
  const organizationTypeWithNoEmptyFields = clearEmptyFields({
    '@id': `${origin}#organization`,
    '@type': 'Organization',
    name: 'Coursera',
    url: 'https://www.coursera.org',
    logo: Imgix.processImage('https://coursera.s3.amazonaws.com/media/coursera-logo-square.png'),
    sameAs: [
      'https://www.facebook.com/Coursera',
      'https://twitter.com/coursera',
      'https://www.linkedin.com/company/coursera',
      'https://www.instagram.com/coursera/',
      'https://www.youtube.com/user/coursera',
    ],
  });

  return organizationTypeWithNoEmptyFields;
};

const createMarkup = (markupJson: $TSFixMe) => ({
  __html: `
    <script type="application/ld+json">
      ${JSON.stringify(markupJson)}
    </script>
  `,
});

// Note: this component exposes the same data as VideoPageGoogleSchemaMarkup,
// but just removes any extra api calls so it can be more performant and in line with the VLPContainer
export const VideoPageGoogleSchemaMarkupV2 = ({
  course,
  courseName,
  courseSlug,
  partner,
  instructors,
  videoItem,
  lesson: videoLesson,
  module: videoModule,
  textTranscript,
  onDemandVideosSpec,
}: Props) => {
  const location = useLocation();

  if (!videoItem) {
    return null;
  }

  const lowestVideoSpecSources =
    onDemandVideosSpec?.sources?.byResolution &&
    Object.values(onDemandVideosSpec?.sources?.byResolution).filter(Boolean)[0];

  const hdVideoSpecSources = onDemandVideosSpec?.sources?.byResolution?.['720P'] ?? lowestVideoSpecSources;

  const lectureName = videoItem.name;

  let lectureDesc = `Watch this video about ${lectureName} in ${courseName}.`;

  if (videoLesson) {
    lectureDesc = `Watch this video about ${lectureName} for ${videoLesson.name} in ${courseName}.`;
  }

  if (videoModule && videoModule.description !== DEFAULT_VLP_META_DESCRIPTION) {
    lectureDesc += ` ${videoModule.description}`;
  } else {
    lectureDesc += constructFallBackDescriptionForVlp(textTranscript);
  }

  const cdpPath = courseSlug && `/learn/${courseSlug}`;
  const uploadDate = course.startDate && moment(course.startDate).toISOString();
  const origin = config.url.domain;

  const markupJson = {
    '@context': 'http://schema.org/',
    '@graph': [
      getOrganizationGraphType(origin),
      clearEmptyFields({
        '@id': `${path.join(origin, location.pathname)}#lecture`, // URL of the public video lecture page
        '@type': 'VideoObject',
        name: lectureName,
        url: path.join(origin, location.pathname),
        description: lectureDesc,
        thumbnailURL: hdVideoSpecSources?.previewImageUrl, // thumbnail URL from video xml sitemap
        contentURL: hdVideoSpecSources?.mp4VideoUrl, // content URL from video xml sitemap
        duration: moment.duration(videoItem.timeCommitment).toISOString(), // duration time from video xml sitemap
        uploadDate, // date the course was launched
        isPartOf: {
          '@id': cdpPath ? `${path.join(origin, cdpPath)}#mooc` : '',
          '@type': 'Course',
          Name: courseName,
          Description: course.description,
          Provider: {
            '@id': 'https://www.coursera.org#corporation',
            '@type': 'Corporation',
            name: 'Coursera',
          },
        },
        transcript: textTranscript,
        author: {
          '@type': 'Person',
          name: getInstructorNames(instructors),
          url: getInstructorURLs(origin, instructors),
        },
        Creator: partner
          ? {
              '@id': `${path.join(origin, partner.shortName)}#institution`,
              '@type': 'CollegeOrUniversity',
              name: partner.name,
              url: path.join(origin, partner.shortName), // URL of partner page on Coursera site
            }
          : {},
        Provider: {
          '@id': `${origin}#organization`,
          '@type': 'Corporation',
          name: 'Coursera',
          url: origin,
        },
      }),
    ],
  };

  return (
    <div className="rc-VideoPageGoogleSchemaMarkupV2">
      <div dangerouslySetInnerHTML={createMarkup(markupJson)} />
    </div>
  );
};

export default VideoPageGoogleSchemaMarkupV2;
